<template>
  <div class="maps-page">
    <h2>🗺️ Карта слогов</h2>

    <div class="two-cols">
      <div class="col col-2">
        <h3 class="col-title">2 буквы</h3>
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th></th>
                <th v-for="v in vowels" :key="v">{{ v }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="c in consonants" :key="c">
                <td class="row-label">{{ c }}</td>
                <td
                  v-for="v in vowels"
                  :key="c + v"
                  class="cell"
                  :style="{ backgroundColor: cellColor2(c + v) }"
                  @mouseenter="hovered2 = c + v"
                  @mouseleave="hovered2 = null"
                >
                  <span class="cell-text">{{ c }}{{ v }}</span>
                  <div v-if="hovered2 === c + v" class="tooltip">
                    <strong>{{ c }}{{ v }}</strong><br>
                    +{{ s2.getStat(c + v).correct }} / −{{ s2.getStat(c + v).wrong }}
                    <template v-if="s2.totalAttempts(c + v) > 0">
                      <br>{{ Math.round(s2.successRate(c + v) * 100) }}% · {{ s2.avgTime(c + v) }}с
                    </template>
                    <template v-else><br>Не изучали</template>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="col col-3">
        <h3 class="col-title">3 буквы</h3>
        <div v-if="s3" class="sections">
          <div v-for="group in syllables3Grouped" :key="group.letter" class="section">
            <div class="section-title">{{ group.letter }}</div>
            <div class="syllable-grid">
              <span
                v-for="syl in group.items"
                :key="syl"
                class="cell cell-sm"
                :style="{ backgroundColor: cellColor3(syl) }"
                @mouseenter="hovered3 = syl"
                @mouseleave="hovered3 = null"
              >
                <span class="cell-text">{{ syl }}</span>
                <div v-if="hovered3 === syl" class="tooltip">
                  <strong>{{ syl }}</strong><br>
                  +{{ s3.getStat(syl).correct }} / −{{ s3.getStat(syl).wrong }}
                  <template v-if="s3.totalAttempts(syl) > 0">
                    <br>{{ Math.round(s3.successRate(syl) * 100) }}% · {{ s3.avgTime(syl) }}с
                  </template>
                  <template v-else><br>Не изучали</template>
                </div>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="legend">
      <span class="legend-item" style="background:#e0e0e0">—</span>
      <span class="legend-item" style="background:hsl(0,70%,55%)">&lt;10%</span>
      <span class="legend-item" style="background:hsl(30,70%,55%)">25%</span>
      <span class="legend-item" style="background:hsl(60,70%,55%)">50%</span>
      <span class="legend-item" style="background:hsl(90,70%,55%)">75%</span>
      <span class="legend-item" style="background:hsl(120,70%,55%)">&gt;90%</span>
    </div>

    <div class="actions">
      <button class="btn-danger" @click="confirmReset">Сбросить всю статистику</button>
    </div>

    <div class="back">
      <router-link to="/">← На главную</router-link>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useSyllableStats, useSyllableStats3 } from '../composables/useSyllableStats.js'

const s2 = useSyllableStats()
const s3 = ref(null)
useSyllableStats3().then(s => { s3.value = s })
const consonants = s2.consonants
const vowels = s2.vowels

const hovered2 = ref(null)
const hovered3 = ref(null)

const syllables3Grouped = computed(() => {
  if (!s3.value) return []
  const all = s3.value.allSyllables()
  const map = {}
  for (const s of all) {
    const first = s[0]
    if (!map[first]) map[first] = []
    if (!map[first].includes(s)) map[first].push(s)
  }
  return Object.keys(map).sort().map(letter => ({ letter, items: map[letter] }))
})

function cellColor2(syllable) {
  return s2.colorForRate(s2.successRate(syllable))
}

function cellColor3(syllable) {
  if (!s3.value) return '#e0e0e0'
  return s3.value.colorForRate(s3.value.successRate(syllable))
}

function confirmReset() {
  if (confirm('Сбросить всю статистику?')) {
    s2.resetStats()
    if (s3.value) s3.value.resetStats()
  }
}
</script>

<style scoped>
.maps-page {
  text-align: center;
  max-width: 100vw;
  overflow-x: hidden;
}

h2 {
  color: #2c3e50;
  margin-bottom: 20px;
}

.two-cols {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.col {
  min-width: 0;
  overflow: hidden;
}

.col-2 {
  flex: 3;
}

.col-3 {
  flex: 4;
}

.col-title {
  font-size: 18px;
  color: #42b883;
  margin-bottom: 12px;
}

.table-wrap {
  overflow: hidden;
}

table {
  border-collapse: separate;
  border-spacing: 2px;
  margin: 0 auto;
  width: 100%;
  table-layout: fixed;
}

th {
  font-size: 13px;
  font-weight: 700;
  color: #888;
  padding: 2px 3px;
}

.row-label {
  font-size: 13px;
  font-weight: 700;
  color: #888;
  padding: 2px 4px;
  text-align: right;
  width: 24px;
}

.cell {
  position: relative;
  padding: 4px 2px;
  border-radius: 4px;
  cursor: default;
  transition: transform 0.15s;
}

.cell:hover {
  transform: scale(1.15);
  z-index: 10;
}

.cell-sm {
  display: inline-block;
  padding: 2px 4px;
  border-radius: 3px;
  position: relative;
  cursor: default;
}

.cell-sm:hover {
  transform: scale(1.2);
  z-index: 10;
}

.cell-text {
  color: white;
  font-weight: 700;
  font-size: 11px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.tooltip {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #2c3e50;
  color: white;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 400;
  white-space: nowrap;
  z-index: 100;
  pointer-events: none;
  text-shadow: none;
}

.tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 5px solid transparent;
  border-top-color: #2c3e50;
}

.sections {
  max-height: 70vh;
  overflow-y: auto;
  overflow-x: hidden;
}

.section {
  margin-bottom: 10px;
}

.section-title {
  font-size: 13px;
  font-weight: 700;
  color: #2980b9;
  margin-bottom: 3px;
}

.syllable-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
}

.legend {
  display: flex;
  gap: 8px;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 20px;
}

.legend-item {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 4px;
  color: white;
  font-size: 11px;
  font-weight: 700;
  text-shadow: 0 1px 1px rgba(0, 0, 0, 0.2);
}

.actions {
  margin-top: 20px;
}

.btn-danger {
  padding: 10px 20px;
  background: #e74c3c;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
}

.btn-danger:hover {
  background: #c0392b;
}

.back {
  margin-top: 16px;
}

.back a {
  color: #7f8c8d;
  text-decoration: none;
  font-size: 16px;
}

.back a:hover {
  color: #42b883;
}
</style>
